/**
 * Category Landing Page Tracking Service
 *
 * Tracks user visits to category landing pages (SRP and PDP).
 * Fires once per page load to capture category-level engagement.
 *
 * API Endpoint: /portal/srp/filter-interaction (same as filter tracking)
 */

import { baseApi } from "@/api/apiConfig";
import { store } from "@/store/store";
import { getSourceType, buildUtmInfo } from "./attributionCapture";
import Cookies from "js-cookie";

/**
 * Map PDP propCategory code to filter-style category value.
 *
 * @param {string|number} propCategory - Category code from pdpInit API
 * @returns {string|null} Filter-style category (e.g., "category=bankowned")
 */
export function getCategoryFromPropCategory(propCategory) {
  if (!propCategory) return null;

  const categoryMap = {
    // String codes
    REO: "category=bankowned",
    FORECLOSURE: "category=foreclosure",
    COMMERCIAL: "category=commercial",
    SHRT_SALE: "category=SHRT_SALE",
    NON_REO: "category=nonbankowned",
    SC: "category=secondchance",
    ABS: "category=absolute",
    NPL: "category=npl",
    // Numeric IDs
    1: "category=bankowned",
    2: "category=SHRT_SALE",
    3: "category=nonbankowned",
    4: "category=foreclosure",
    5: "category=secondchance",
    6: "category=absolute",
    7: "category=npl",
    8: "category=commercial",
  };

  return categoryMap[propCategory] || `category=${String(propCategory)}`;
}

/**
 * Determine the entry source for PAGE_VIEW events.
 * Returns: 'DIRECT', 'INTERNAL_NAVIGATION', or 'EXTERNAL_REFERRAL'
 */
function getEntrySource() {
  const sourceType = getSourceType();

  switch (sourceType) {
    case "internal":
      return "INTERNAL_NAVIGATION";
    case "paid":
    case "campaign":
    case "referral":
      return "EXTERNAL_REFERRAL";
    case "direct":
    default:
      return "DIRECT";
  }
}

// Key for storing last tracked PAGE_VIEW URL per visitor
const LAST_PAGE_VIEW_KEY = "hubzu_last_page_view";

/**
 * Check if this is a page reload (not back/forward or fresh navigation).
 */
function isPageReload() {
  const navEntries = performance.getEntriesByType("navigation");
  if (navEntries.length > 0) {
    return navEntries[0].type === "reload";
  }
  return false;
}

/**
 * Track a category landing page view via API.
 * Skips on page reload if same URL was already tracked by same visitor.
 * Fires on back/forward navigation and fresh visits.
 *
 * @param {object} params
 * @param {string} params.categoryType - Category name (e.g., "Commercial", "Bank-Owned")
 * @param {string} params.pageName - Page identifier ("SRP" or "PDP")
 * @param {string} [params.propertyId] - Property ID (for PDP context)
 */
export function trackCategoryPageView({
  categoryType,
  pageName,
  propertyId = null,
  isInternal = null,
}) {
  if (!categoryType) return; // Skip if no category determined

  // On reload: skip if same visitor already tracked this exact URL
  if (isPageReload()) {
    const visitorId = Cookies.get("visitorId") || "";
    const currentUrl = window.location.pathname + window.location.search;
    const lastTracked = sessionStorage.getItem(LAST_PAGE_VIEW_KEY);
    if (lastTracked === `${visitorId}|${currentUrl}`) {
      return; // Same visitor, same URL, reload — skip duplicate
    }
  }

  // Capture entry source — use pre-captured isInternal flag if provided (for PDP where delay is long)
  const entrySource =
    isInternal === true ? "INTERNAL_NAVIGATION" : getEntrySource();

  // Delay 3s to allow user state to hydrate after page load
  setTimeout(() => {
    try {
      const state = store.getState();
      const user = state.user;
      const visitorId = Cookies.get("visitorId") || "";

      const payload = {
        filterType: categoryType,
        eventType: "PAGE_VIEW",
        propertyId: propertyId || null,
        userType: user.isUserLoggedIn ? "LOGGED_IN" : "GUEST",
        visitorId: visitorId,
        userId: user.user?.realbidUserId || null,
        sourcePage: pageName,
        entrySource: entrySource,
        utmInfo: buildUtmInfo(),
      };

      // Store this as last tracked PAGE_VIEW for reload dedup
      const currentUrl = window.location.pathname + window.location.search;
      sessionStorage.setItem(LAST_PAGE_VIEW_KEY, `${visitorId}|${currentUrl}`);

      // Fire-and-forget API call
      baseApi.post("/portal/srp/filter-interaction", payload).catch((err) => {
        console.error(
          "[CategoryPageTracking] API call failed:",
          err?.message || err,
        );
      });
    } catch (err) {
      console.error("[CategoryPageTracking] Error building payload:", err);
    }
  }, 3000);
}
